import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Typography,
} from '@material-ui/core';
import { FileText } from 'react-feather';
import {
    getOutboundPreviewMediaType,
    isImagePreview,
} from 'src/views/OmniChannel/utils/outboundMultimedia';
import { useDocumentsGridItemStyles } from './documentsGridStyles';
import type { TDocumentsGridItemProps } from './types';

export const DocumentsGridItem = ({
    mediaUrl,
    mediaContentType,
    mediaAlt = 'document',
    title,
    subtitle,
    footer,
    selected = false,
    showSelection = false,
    onClick,
    onMediaPreview,
}: TDocumentsGridItemProps) => {
    const classes = useDocumentsGridItemStyles();

    const hasMedia = Boolean(mediaUrl?.trim());
    const mediaType = hasMedia
        ? getOutboundPreviewMediaType(mediaAlt, mediaContentType, mediaUrl)
        : null;
    const showImage =
        hasMedia &&
        (mediaType === 'image' || isImagePreview(mediaUrl!, mediaContentType));

    const handleMediaClick = (event: React.MouseEvent) => {
        if (!onMediaPreview) return;

        event.stopPropagation();
        onMediaPreview();
    };

    return (
        <Card
            className={`${classes.card} ${selected ? classes.cardSelected : ''}`}
            elevation={0}
            onClick={onClick}
        >
            {showSelection && (
                <Box className={classes.selectionBadge}>
                    <Checkbox
                        checked={selected}
                        color="primary"
                        size="small"
                        tabIndex={-1}
                    />
                </Box>
            )}

            {hasMedia && (
                <Box
                    className={`${classes.imageBox} ${
                        onMediaPreview ? classes.imageBoxInteractive : ''
                    }`}
                    onClick={onMediaPreview ? handleMediaClick : undefined}
                    role={onMediaPreview ? 'button' : undefined}
                    tabIndex={onMediaPreview ? 0 : undefined}
                    onKeyDown={(event) => {
                        if (!onMediaPreview) return;

                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            onMediaPreview();
                        }
                    }}
                >
                    {showImage ? (
                        <img
                            src={mediaUrl}
                            alt={mediaAlt}
                            className={classes.image}
                        />
                    ) : (
                        <Box className={classes.filePreview}>
                            <FileText size={40} />
                        </Box>
                    )}
                </Box>
            )}

            <CardContent className={classes.content}>
                {title ? (
                    <Typography
                        color="inherit"
                        variant="body1"
                        className={classes.title}
                    >
                        {title}
                    </Typography>
                ) : null}

                {subtitle && (
                    <Typography
                        color="inherit"
                        variant="body1"
                        className={classes.secondaryText}
                        style={{ marginTop: 4 }}
                    >
                        {subtitle}
                    </Typography>
                )}

                {footer}
            </CardContent>
        </Card>
    );
};
